import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { calculateGematria } from '../../utils/calculator';
import { useAppTheme } from '../../utils/useAppTheme';
import bibleDB from '../../data/bibleGematriaDB.json';
import phraseDB from '../../data/biblePhraseDB.json';
import WebVerseReference from '../components/WebVerseReference';

const DEFAULT_CIPHERS = {
  'English Ordinal': true,
  'Full Reduction': true,
  'Single Reduction': true,
  'Full Reduction KV': true,
  'Single Reduction KV': true,
  'Extended English': true,
  'Francis Bacon': true,
  'Franc Baconis': true,
  'Satanic': true,
  'Reverse Ordinal': true,
  'Reverse Full Reduction': true,
  'Reverse Single Reduction': true,
  'Reverse Full Reduction EP': true,
  'Reverse Single Reduction EP': true,
  'Reverse Extended': true,
  'Reverse Francis Bacon': true,
  'Reverse Franc Baconis': true,
  'Reverse Satanic': true,
  'Jewish Reduction': true,
  'Jewish Ordinal': true,
  'Jewish': true,
  'ALW Kabbalah': true,
  'KFW Kabbalah': true,
  'LCH Kabbalah': true,
  'English Sumerian': true,
  'Reverse English Sumerian': true,
  'Primes': true,
  'Trigonal': true,
  'Squares': true,
  'Reverse Primes': true,
  'Reverse Trigonal': true,
  'Reverse Squares': true,
  'Septenary': true,
  'Chaldean': true,
  'Keypad': true,
  'Fibonacci': true,
  'Alphanumeric Qabbala': true,
  'Elizabethan 360': true,
  'William G. Gray': true,
  'Synx': true,
  'Beatus of Liebana': true,
  'Prime Qabalah': true,
  'False Kabbalah': true,
};

export default function BibleMatchPage({ selectedCiphers }) {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [inputText, setInputText] = useState('');
  const [numberInput, setNumberInput] = useState('');
  const [searchMode, setSearchMode] = useState('text'); // 'text' or 'number'
  const [bibleMatches, setBibleMatches] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [expandedCiphers, setExpandedCiphers] = useState({});
  const [activeTab, setActiveTab] = useState('phrases');

  const ciphersToUse = selectedCiphers && Object.keys(selectedCiphers).length > 0
    ? selectedCiphers
    : DEFAULT_CIPHERS;

  useEffect(() => {
    if (searchMode !== 'text') return;
    const timer = setTimeout(() => {
      if (inputText.trim()) {
        findMatches(inputText);
      } else {
        setBibleMatches({});
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [inputText, ciphersToUse, searchMode]);

  useEffect(() => {
    if (searchMode !== 'number') return;
    const timer = setTimeout(() => {
      if (numberInput.trim()) {
        findByNumber(numberInput);
      } else {
        setBibleMatches({});
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [numberInput, searchMode]);

  const findMatches = async (text) => {
    setIsCalculating(true);
    try {
      const calcResults = await calculateGematria(text, ciphersToUse, false);

      const matches = {};
      calcResults.forEach(result => {
        const cipherName = result.name;
        const value = result.totalValue;
        const words = (bibleDB[cipherName] && bibleDB[cipherName][value]) || [];
        const phrases = (phraseDB[cipherName] && phraseDB[cipherName][value]) || [];
        if (words.length > 0 || phrases.length > 0) {
          matches[cipherName] = { value, words, phrases };
        }
      });

      setBibleMatches(matches);
    } catch (error) {
      console.error('Error calculating Bible matches:', error);
    }
    setIsCalculating(false);
  };

  const findByNumber = (numStr) => {
    const value = parseInt(numStr);
    if (isNaN(value)) {
      setBibleMatches({});
      return;
    }

    setIsCalculating(true);

    const matches = {};

    Object.keys(bibleDB).forEach(cipherName => {
      const words = (bibleDB[cipherName] && bibleDB[cipherName][value]) || [];
      const phrases = (phraseDB[cipherName] && phraseDB[cipherName][value]) || [];
      if (words.length > 0 || phrases.length > 0) {
        matches[cipherName] = { value, words, phrases };
      }
    });

    Object.keys(phraseDB).forEach(cipherName => {
      if (!matches[cipherName]) {
        const phrases = (phraseDB[cipherName] && phraseDB[cipherName][value]) || [];
        if (phrases.length > 0) {
          matches[cipherName] = { value, words: [], phrases };
        }
      }
    });

    setBibleMatches(matches);
    setIsCalculating(false);
  };

  const toggleExpand = (cipher) => {
    setExpandedCiphers(prev => ({ ...prev, [cipher]: !prev[cipher] }));
  };

  const getFilteredMatches = () => {
    const filtered = {};
    Object.keys(bibleMatches).forEach(cipher => {
      const match = bibleMatches[cipher];
      if (activeTab === 'phrases' && match.phrases.length > 0) {
        filtered[cipher] = match;
      } else if (activeTab === 'words' && match.words.length > 0) {
        filtered[cipher] = match;
      }
    });
    return filtered;
  };

  const getTotalPhraseMatches = () => {
    return Object.values(bibleMatches).reduce((sum, m) => sum + m.phrases.length, 0);
  };

  const getTotalWordMatches = () => {
    return Object.values(bibleMatches).reduce((sum, m) => sum + m.words.length, 0);
  };

  const filteredMatches = getFilteredMatches();

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>📖 Bible Match</Text>
        <Text style={styles.subtitle}>
          Find Bible words & phrases (KJV) with the same gematria value
        </Text>
      </View>

      <View style={styles.inputSection}>
        {/* Search Mode Toggle */}
        <View style={styles.searchModeRow}>
          <TouchableOpacity
            style={[styles.searchModeBtn, searchMode === 'text' && styles.searchModeBtnActive]}
            onPress={() => { setSearchMode('text'); setBibleMatches({}); }}
          >
            <Text style={[styles.searchModeText, searchMode === 'text' && styles.searchModeTextActive]}>
              🔤 By Text
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.searchModeBtn, searchMode === 'number' && styles.searchModeBtnActive]}
            onPress={() => { setSearchMode('number'); setBibleMatches({}); }}
          >
            <Text style={[styles.searchModeText, searchMode === 'number' && styles.searchModeTextActive]}>
              🔢 By Number
            </Text>
          </TouchableOpacity>
        </View>

        {searchMode === 'text' ? (
          <TextInput
            style={styles.input}
            placeholder="Enter a word or phrase..."
            placeholderTextColor={colors.textSecondary}
            selectionColor={colors.primary}
            value={inputText}
            onChangeText={setInputText}
          />
        ) : (
          <TextInput
            style={styles.input}
            placeholder="Enter a number (e.g. 33, 74, 666)..."
            placeholderTextColor={colors.textSecondary}
            selectionColor={colors.primary}
            value={numberInput}
            onChangeText={setNumberInput}
            keyboardType="numeric"
          />
        )}
      </View>

      {/* Tab Switcher */}
      {Object.keys(bibleMatches).length > 0 && (
        <View style={styles.tabRow}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'phrases' && styles.tabActive]}
            onPress={() => setActiveTab('phrases')}
          >
            <Text style={[styles.tabText, activeTab === 'phrases' && styles.tabTextActive]}>
              📜 Phrases ({getTotalPhraseMatches()})
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'words' && styles.tabActive]}
            onPress={() => setActiveTab('words')}
          >
            <Text style={[styles.tabText, activeTab === 'words' && styles.tabTextActive]}>
              📝 Words ({getTotalWordMatches()})
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {isCalculating && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={styles.loadingText}>Searching Bible database...</Text>
        </View>
      )}

      {!isCalculating && ((searchMode === 'text' && inputText.trim()) || (searchMode === 'number' && numberInput.trim())) && Object.keys(bibleMatches).length === 0 && (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyIcon}>🔍</Text>
          <Text style={styles.emptyText}>No Bible matches found</Text>
          <Text style={styles.emptySubtext}>
            {searchMode === 'text' ? 'Try a different word or phrase' : 'Try a different number'}
          </Text>
        </View>
      )}

      {!isCalculating && Object.keys(filteredMatches).length === 0 && Object.keys(bibleMatches).length > 0 && (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>
            No {activeTab === 'phrases' ? 'phrase' : 'word'} matches for this input
          </Text>
          <Text style={styles.emptySubtext}>Try the other tab</Text>
        </View>
      )}

      {!isCalculating && Object.keys(filteredMatches).length > 0 && (
        <View style={styles.resultsContainer}>
          {Object.keys(filteredMatches).map(cipherName => {
            const match = filteredMatches[cipherName];
            const isExpanded = expandedCiphers[cipherName] === true;
            const items = activeTab === 'phrases' ? match.phrases : match.words;
            const preview = activeTab === 'phrases' ? items[0].p : items[0].w;

            return (
              <View key={cipherName} style={styles.cipherCard}>
                <TouchableOpacity
                  style={styles.cipherRow}
                  onPress={() => toggleExpand(cipherName)}
                >
                  <View style={styles.cipherInfo}>
                    <Text style={styles.cipherName}>{cipherName}</Text>
                    <Text style={styles.cipherMeta}>
                      = {match.value} • {items.length} match{items.length !== 1 ? 'es' : ''}
                    </Text>
                  </View>
                  <View style={styles.previewSection}>
                    <Text style={styles.previewText} numberOfLines={1}>{preview}</Text>
                    <Text style={styles.expandArrow}>{isExpanded ? '▼' : '▶'}</Text>
                  </View>
                </TouchableOpacity>

                {isExpanded && (
                  <View style={styles.expandedList}>
                    {activeTab === 'phrases' ? (
                      match.phrases.map((entry, idx) => (
                        <View key={idx} style={[styles.tableRow, idx % 2 === 0 && styles.tableRowAlt]}>
                          <Text style={[styles.tableCell, { flex: 3 }]}>{entry.p}</Text>
                          <WebVerseReference
                            reference={entry.r}
                            style={{ flex: 1, alignItems: 'flex-end' }}
                          />
                        </View>
                      ))
                    ) : (
                      <>
                        <View style={styles.tableHeader}>
                          <Text style={[styles.tableHeaderText, { flex: 2 }]}>Word</Text>
                          <TouchableOpacity style={{ flex: 1 }} onPress={() => alert('First Ref: The first verse in the Bible (KJV) where this word appears.\n\nExample: "Gen 15:6" = Genesis chapter 15, verse 6.')}>
                            <Text style={[styles.tableHeaderText, { cursor: 'pointer' }]}>First Ref ⓘ</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={{ width: 60 }} onPress={() => alert('In Bible: How many times this word appears in the entire KJV Bible.\n\nExample: "×172" means 172 occurrences across all 66 books.')}>
                            <Text style={[styles.tableHeaderText, { textAlign: 'right', cursor: 'pointer' }]}>In Bible ⓘ</Text>
                          </TouchableOpacity>
                        </View>
                        {match.words.map((entry, idx) => (
                          <View key={idx} style={[styles.tableRow, idx % 2 === 0 && styles.tableRowAlt]}>
                            <Text style={[styles.tableCell, { flex: 2, fontWeight: '500', textTransform: 'capitalize' }]}>
                              {entry.w}
                            </Text>
                            <WebVerseReference
                              reference={entry.r}
                              style={{ flex: 1, alignItems: 'flex-start' }}
                            />
                            <Text style={[styles.tableCell, { width: 60, textAlign: 'right', color: colors.textSecondary }]}>
                              ×{entry.c}
                            </Text>
                          </View>
                        ))}
                      </>
                    )}
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}

      {((searchMode === 'text' && !inputText.trim()) || (searchMode === 'number' && !numberInput.trim())) && (
        <View style={styles.infoBox}>
          <Text style={styles.infoTitle}>How it works</Text>
          <Text style={styles.infoText}>
            Type any word or phrase to find Bible matches with the same gematria value.
            Switch between the two tabs to view:
          </Text>
          <Text style={styles.infoItem}>📜 <Text style={{ fontWeight: '600' }}>Phrases</Text> — Famous verses & well-known quotes from the Bible</Text>
          <Text style={styles.infoItem}>📝 <Text style={{ fontWeight: '600' }}>Words</Text> — Individual words from the KJV Bible, sorted by frequency</Text>
          <Text style={styles.infoFooter}>
            📊 12,674 words + 703 famous phrases from 66 KJV books • 43 ciphers
          </Text>
        </View>
      )}
    </ScrollView>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    maxWidth: 900,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: colors.textSecondary,
  },
  inputSection: {
    marginBottom: 20,
  },
  searchModeRow: {
    flexDirection: 'row',
    marginBottom: 12,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: colors.border,
  },
  searchModeBtn: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    backgroundColor: colors.surface,
    cursor: 'pointer',
  },
  searchModeBtnActive: {
    backgroundColor: colors.primary,
  },
  searchModeText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  searchModeTextActive: {
    color: colors.onPrimary,
  },
  input: {
    color: colors.text,
    backgroundColor: colors.inputBackground,
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 14,
    fontSize: 18,
    outlineColor: colors.primary,
  },
  tabRow: {
    flexDirection: 'row',
    marginBottom: 16,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: colors.primary,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  tabActive: {
    backgroundColor: colors.primary,
  },
  tabText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  tabTextActive: {
    color: colors.onPrimary,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 30,
  },
  loadingText: {
    marginLeft: 10,
    color: colors.textSecondary,
    fontSize: 15,
  },
  emptyContainer: {
    alignItems: 'center',
    padding: 40,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 18,
    color: colors.textSecondary,
    fontWeight: '500',
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 4,
  },
  resultsContainer: {
    marginTop: 8,
  },
  cipherCard: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: 8,
    marginBottom: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.lightBorder,
  },
  cipherRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    cursor: 'pointer',
  },
  cipherInfo: {
    flex: 1,
  },
  cipherName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
  },
  cipherMeta: {
    fontSize: 13,
    color: colors.primary,
    marginTop: 2,
  },
  previewSection: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '45%',
  },
  previewText: {
    fontSize: 14,
    color: colors.secondary,
    fontWeight: '500',
    textTransform: 'capitalize',
    marginRight: 10,
  },
  expandArrow: {
    fontSize: 11,
    color: colors.textSecondary,
  },
  expandedList: {
    borderTopWidth: 1,
    borderTopColor: colors.lightBorder,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: colors.mutedSurface,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightBorder,
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightBorder,
    alignItems: 'center',
  },
  tableRowAlt: {
    backgroundColor: colors.mutedSurface,
  },
  tableCell: {
    fontSize: 14,
    color: colors.text,
  },
  infoBox: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: 10,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.border,
    marginTop: 10,
  },
  infoTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 12,
  },
  infoText: {
    fontSize: 15,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 12,
  },
  infoItem: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 24,
    marginBottom: 4,
  },
  infoFooter: {
    fontSize: 13,
    color: colors.textSecondary,
    fontStyle: 'italic',
    lineHeight: 20,
    marginTop: 12,
  },
});
